import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link, useNavigate } from "react-router-dom";

import { ExpertProjectDetails } from "../../../pages/expert-project/types";
import style from "./expert-project-card.module.scss";
import { LocalDayjs } from "../../../utils/timezoneService";
import CustomBtnFilled from "../../../atoms/form-molecules/CustomBtnFilled";
import { useBoolean } from "../../../utils/hooks/useBoolean";
import { AppRoutes } from "../../../constants";
import { useSnackbar } from "notistack";
import { isAdmin } from "../../../utils/role";
import calender from "../../../assets/images/expert/calendar_expert.png";
import TooltipIcons from "../../../atoms/project-card-footer-icons";
import { useState } from "react";
import DialogModal from "../../../atoms/dialog";
import { AGENDA_STATUS, INVITATION_STATUS } from "../../../constants/expert-project.constants";
import { Box } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AgendaModal from "../agenda-modal";

type Props = {
    project: ExpertProjectDetails,
    refetch: () => Promise<void>
}

interface ProjectPOC {
    id: string;
    name: string;
    email: string;
    mobile: string;
}

const statusChipMap = {
    "Open": `${style.statusChip} ${style.statusChipOpen}`,
    "Closed": `${style.statusChip} ${style.statusChipClosed}`
}

export default function ExpertProjectCard({ project, refetch }: Props) {
    const [isPOCDialogOpen, setIsPOCDialogOpen] = useState(false);
    const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false);
    const token = localStorage.getItem("token");
    const navigate = useNavigate();
    const { value: isAgendaOpen, setFalse: closeAgenda, setTrue: openAgenda } = useBoolean();
    const { enqueueSnackbar } = useSnackbar();
    
    // Extract values from project for better readability
    const projectReceivingDateForExpert = project?.meta?.first_invite_sent_to_expert?.date;
    const invitationStatus = project?.expert_invitation;
    const agendaStatus = project?.agenda_status;
    const agendaSharedOn = project?.agenda_shared_on;
    const projectStatus = project.fk_project_value?.status;
    const projectPOC: ProjectPOC | undefined = project?.fk_project_value?.account_manager_value;
    
    const viewInvitationButton = (invitationStatus === INVITATION_STATUS.INVITED && projectStatus !== 'Closed') ? (
        <CustomBtnFilled
            label="View Invitation"
            variant="contained"
            onClick={() => {
                let redirect_url = "";
                if (project.meta && project.meta.project_invitation_link) {
                    const projectInvitationLink = String(project.meta.project_invitation_link);
                    redirect_url = projectInvitationLink.includes("?")
                        ? `${projectInvitationLink}&token=${token}`
                        : `${projectInvitationLink}?token=${token}`;
                    window.open(redirect_url, "_target", "noopener,noreferrer")
                } else {
                    console.error("Missing project_invitation_link in project meta.");
                    enqueueSnackbar("Missing project invitation link", {
                        variant: "warning"
                    })
                }
            }
            }
        />
    ) : (invitationStatus === INVITATION_STATUS.ACCEPTED ? (
        <CustomBtnFilled
            label="View Invitation"
            variant="outlined"
            onClick={() => {
                setIsInvitationDialogOpen(true);
            }}
        />
    ) : <></>)
    
    const agendaStatusButton = (
        project.expert_invitation === INVITATION_STATUS.ACCEPTED && agendaStatus && (
            <CustomBtnFilled
                label="View Agenda"
                variant="contained"
                onClick={() => {
                    openAgenda()
                }}
            />
        )
    )

    return (
        <>
            <div className={style.card}>
                <div>
                    <div className={style.flexBtwn}>
                        <Tooltip title={project.fk_project_value.external_topic} arrow>
                            <p className={style.heading}>{project.fk_project_value.external_topic}</p>
                        </Tooltip>
                        <Tooltip title={projectStatus} arrow>
                            <div className={statusChipMap[projectStatus as keyof typeof statusChipMap]}>
                                {projectStatus}
                            </div>
                        </Tooltip>
                    </div>
                    <div className={style.divider}></div>
                    <div className={style.projectDetailsContainer}>
                        {projectPOC && (
                            <TitleValueTable
                                title={"POC:"}
                                text={projectPOC?.name}
                                onClick={() => { setIsPOCDialogOpen(true) }}
                            />
                        )}
                        {projectReceivingDateForExpert && (
                            <TitleValueTable
                                title={"Project Receiving Date:"}
                                text={LocalDayjs(projectReceivingDateForExpert).format("DD MMM YYYY")}
                            />
                        )}
                        {invitationStatus && (
                            <TitleValueTable
                                title={"Invitation Status:"}
                                text={INVITATION_STATUS[invitationStatus.toUpperCase() as keyof typeof INVITATION_STATUS]}
                            />
                        )}
                        {agendaStatus && (
                            <TitleValueTable
                                title={"Agenda Status:"}
                                text={AGENDA_STATUS[agendaStatus as keyof typeof AGENDA_STATUS]}
                            />
                        )}
                        {agendaSharedOn &&
                            <TitleValueTable
                                title={"Agenda Shared On:"}
                                text={LocalDayjs(agendaSharedOn).format("DD MMM YYYY")}
                            />
                        }
                    </div>
                </div>
                <div className={style.projectContentFooter}>
                    <div className={style.statsContainer}>
                        <Stats text={project.calls_scheduled?.toString() || "0"} title={"Calls Scheduled"} />
                        <Stats text={project.calls_completed?.toString() || "0"} title={"Calls Done"} onClick={() => { navigate(AppRoutes.CALLS + "?project_id=" + project.fk_project_value?.id) }} />
                    </div>
                    <div className={style.flex} style={{ marginTop: "10px" }}>
                        <TooltipIcons
                            icon={calender}
                            isIcon={true}
                            title="Calender"
                            isDisabled={project.fk_project_value?.status == 'Closed'}
                            handleClick={() => navigate(AppRoutes.CALENDER + "?id=" + project.fk_project_value?.id)}
                        />
                        {viewInvitationButton}
                        {agendaStatusButton}
                    </div>
                </div>
            </div>
            {isAgendaOpen &&
                <AgendaModal
                    handleClose={closeAgenda}
                    isOpen={isAgendaOpen}
                    expertInvitationStatus={project.expert_invitation}
                    peId={project.id}
                    refetch={refetch}
                    projectId={project.fk_project_value?.id}
                    agendaId={null}
                />
            }
            
            {/* POC Details Dialog */}
            {projectPOC &&
                <DialogModal isOpen={isPOCDialogOpen} handleClose={() => { setIsPOCDialogOpen(false) }} title={"POC Details"} isFullScreen={false}>
                    <div className={style.pocDetailsContainer}>
                        <TitleValueTable
                            title={"Name:"}
                            text={projectPOC?.name}
                        />
                        <TitleValueTable
                            title={"Email:"}
                            text={projectPOC?.email}
                        />
                        <TitleValueTable
                            title={"Phone:"}
                            text={projectPOC?.mobile}
                        />
                    </div>
                </DialogModal>
            }
            
            {/* Invitation Details Dialog */}
            {invitationStatus === INVITATION_STATUS.ACCEPTED && (
                <DialogModal isOpen={isInvitationDialogOpen} handleClose={() => { setIsInvitationDialogOpen(false) }} title={"Invitation Details"}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
                        <Typography variant="h6">
                            {project?.fk_project_value?.external_topic || "Project Topic"}
                        </Typography>
                        {
                            project?.fk_project_value?.description &&
                            <Typography dangerouslySetInnerHTML={{ __html: project?.fk_project_value?.description }} variant="body1">
                            </Typography>
                        }
                        <Typography variant="body1" sx={{ fontStyle: "italic", fontSize: "12px" }}>
                            Please confirm your interest in participating by accepting this invite. We'll be happy to share further details upon receiving confirmation from your end. Please note that this is a preliminary step and not a confirmation of the engagement. Upon accepting, you can review the project details & timelines, and then choose to participate/opt-out.
                        </Typography>
                        <Typography variant="body1" sx={{ fontStyle: "italic", fontSize: "12px" }}>
                            Please refrain from discussing any confidential, proprietary and any material non-public information/ un-published price sensitive information during the call. To gain a clear understanding of the guidelines and rules for this engagement, we encourage you to revisit the expert tutorial and the agreement. Infollion, along with its client, retains the right to record the call for reference in the context of this project. By accepting this assignment, you are implicitly granting consent for the call to be recorded.
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            bgcolor: 'rgba(76, 175, 80, 0.1)',
                            borderRadius: 1,
                            border: '1px solid #4caf50'
                        }}>
                            <CheckCircleIcon color="success" />
                            <Typography variant="body1" fontWeight="500">
                                Accepted.
                            </Typography>
                        </Box>
                    </Box>
                </DialogModal>
            )}
        </>
    )
}

function TitleValueTable({ text, title, text_link, onClick, additionalContent }: { text: string, title: string, text_link?: string, onClick?: () => void, additionalContent?: React.ReactNode }) {
    return (
        <div className={style.grid}>
            <p className={style.title}>{title}</p>
            {(text_link && text !== "0") ? (
                <div className={style.valueWrapper}>
                    <Typography sx={{
                        textDecoration: "underline",
                        color: "rgb(236, 147, 36)",
                        fontWeight: "500"
                    }}>
                        <Link
                            to={text_link}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {text}
                        </Link>
                    </Typography>
                    {additionalContent}
                </div>
            ) : onClick ? (
                <div className={style.valueWrapper}>
                    <Typography sx={{
                        textDecoration: "underline",
                        color: "rgb(236, 147, 36)",
                        fontWeight: "500",
                        cursor: "pointer"
                    }} onClick={onClick}>
                        {text}
                    </Typography>
                    {additionalContent}
                </div>
            ) : (
                <div className={style.valueWrapper}>
                    <p className={style.value}>{text}</p>
                    {additionalContent}
                </div>
            )}
        </div>
    )
}

function Stats({ text, title, onClick }: { text: string | number, title: string, onClick?: () => void }) {
    return (
        <div style={{ cursor: onClick ? "pointer" : "initial" }} onClick={onClick} className={style.stats}>
            <p className={style.statsText}>{text}</p>
            <p className={style.statsTitle}>{title}</p>
        </div>
    )
}